import React, { useEffect } from "react";
import { Button } from "react-bootstrap";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { useGetAssistantsQuery } from "../../app/services/chatbot-rtk.services";
import { store } from "../../app/store";
import ApiLoading from "../../components/ui/ApiLoading";
import ProBadge from "../../components/ui/ProBadge";
import { selectClient } from "../dashboard.slice";
import {
  assistantSelectors,
  handleResetWizard,
  handleSetAssistantType,
  wizardState
} from "./botSlice";
import { ConnectForm } from "./BotWizard";
import { AIKittyType } from "./types";

const AssistantTemplates = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const wizard = useAppSelector(wizardState);
  const client = useAppSelector(selectClient);

  const assistants = assistantSelectors.selectAll(store.getState());

  const { isLoading, isError, isSuccess, refetch } = useGetAssistantsQuery({
    language: i18n.language
  });

  const { setValue } = useFormContext();

  const hasRequiredPermission = (assistantType: string) => {
    const targetBotCofig = client?.botConfigs.find(
      (item) => item.botName === wizard.bot_code
    );

    if (targetBotCofig) {
      const acceptableModels = targetBotCofig.config.models;

      return acceptableModels ? acceptableModels.includes(assistantType) : true;
    }
  };

  useEffect(() => {
    if (wizard.bot_config && (wizard.bot_config as AIKittyType).assistant)
      setValue("assistant_type", (wizard.bot_config as AIKittyType).assistant);
  }, [wizard.bot_config]);

  // Initially select first item
  useEffect(() => {
    if (
      isSuccess &&
      !wizard.isInEditMode &&
      (wizard.bot_config as AIKittyType).assistant === ""
    ) {
      if (hasRequiredPermission("free")) {
        dispatch(handleSetAssistantType("free"));
        setValue("assistant_type", "free");
      }
    }
  }, [
    isSuccess,
    wizard.isInEditMode,
    (wizard.bot_config as AIKittyType).assistant
  ]);

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <div className="tw-px-8 tw-w-full tw-justify-center tw-flex-col tw-items-center tw-flex tw-min-h-[50vh]">
        <ApiLoading />
      </div>
    );
  }

  if (isError) {
    content = (
      <div>
        {t("Wizard/general/key10")}
        <Button variant="link" onClick={refetch}>
          {t("Wizard/general/key11")}
        </Button>
      </div>
    );
  }

  if (isSuccess) {
    content = (
      <ConnectForm>
        {({ setValue }: UseFormReturn<FieldValues, any>) => (
          <div className="tw-grid tw-grid-cols-12 tw-gap-5">
            {[
              ...(wizard.category === "clone"
                ? assistants.filter(
                    (assistant) => assistant.code === "self_clone"
                  )
                : assistants)
            ].map((item, index: number) => (
              <div
                key={index}
                className={`lg:tw-col-span-3 md:tw-col-span-6 tw-col-span-12 tw-w-full tw-bg-white tw-shrink tw-shadow tw-flex-col tw-justify-start tw-items-start tw-inline-flex tw-border-solid tw-rounded-lg tw-relative ${
                  wizard.isInEditMode || !hasRequiredPermission(item.code)
                    ? "tw-cursor-not-allowed"
                    : ""
                } ${
                  (wizard.bot_config as AIKittyType).assistant === item.code
                    ? "tw-border-4 tw-border-blue-500"
                    : "tw-border-4 tw-border-transparent"
                } ${
                  wizard.isInEditMode &&
                  (wizard.bot_config as AIKittyType).assistant !== item.code
                    ? "tw-hidden"
                    : "tw-inline-flex"
                } `}
                onClick={() => {
                  if (
                    !wizard.isInEditMode &&
                    hasRequiredPermission(item.code)
                  ) {
                    dispatch(handleSetAssistantType(item.code));
                    setValue("assistant_type", item.code);
                  }
                }}
                onKeyDown={() => {
                  if (
                    !wizard.isInEditMode &&
                    hasRequiredPermission(item.code)
                  ) {
                    dispatch(handleSetAssistantType(item.code));
                    setValue("assistant_type", item.code);
                  }
                }}
                role="button"
                aria-label="assistant-selector"
                tabIndex={0}
              >
                {wizard.isInEditMode &&
                  (wizard.bot_config as AIKittyType).assistant !==
                    item.code && (
                    <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-bg-gray-200 tw-bg-opacity-50" />
                  )}
                <div className="tw-self-stretch tw-h-[200px] tw-bg-blue-200 tw-rounded-tl-lg tw-rounded-tr-lg tw-relative">
                  {!hasRequiredPermission(item.code) && (
                    <div className="tw-absolute tw-top-3 tw-right-3">
                      <ProBadge
                        onClose={() => {
                          dispatch(handleResetWizard());
                        }}
                        filled
                      />
                    </div>
                  )}

                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="img-fluid tw-object-cover tw-h-[200px] tw-w-full tw-rounded-tl-lg tw-rounded-tr-lg tw-object-top"
                  >
                    <source
                      src={`${item.images?.movie}#t=0.001`}
                      type="video/mp4"
                    />
                    <track kind="captions" />
                  </video>
                </div>
                <div className="tw-self-stretch tw-px-6 tw-pt-4 tw-pb-8 tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
                  <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
                    <div className="tw-text-gray-900 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                      {item.name}
                    </div>
                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      {item.description}
                    </div>
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mt-3">
                    {item.skills?.map((sk) => (
                      <div
                        key={sk}
                        className="tw-px-2.5 tw-py-0.5 tw-bg-blue-50 tw-rounded-[10px] tw-justify-center tw-items-center tw-inline-flex"
                      >
                        <div className="tw-text-center tw-text-blue-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                          {sk}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ConnectForm>
    );
  }

  return (
    <ConnectForm>
      {({ formState: { errors } }: UseFormReturn<FieldValues, any>) => (
        <div className="md:tw-px-8 tw-px-1 tw-flex-col tw-justify-start tw-tw-items-start tw-gap-8 tw-inline-flex tw-w-full">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
            <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                {t("Wizard/AssistantTemplates/key1")}
              </div>
              <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                {t("Wizard/AssistantTemplates/key2")}
              </div>
            </div>

            {errors.assistant_type && (
              <div className="tw-self-stretch tw-text-red-600 tw-text-xs tw-font-['Lexend'] tw-leading-none tw-block tw-mb-4">
                {errors?.assistant_type?.message as string}
              </div>
            )}

            {content}
          </div>
        </div>
      )}
    </ConnectForm>
  );
};

export default AssistantTemplates;
